// ─── Weight Tracker Utilities ──────────────────────────────────────────────────
// Unit conversions, formatting, date helpers
// Unit preference support included

// ─── Weight Unit Conversions ───────────────────────────────────────────────────
export function kgToLbs(kg) {
  return kg * 2.20462;
}

export function lbsToKg(lbs) {
  return lbs * 0.453592;
}

export function cmToInches(cm) {
  return cm / 2.54;
}

export function inchesToCm(inches) {
  return inches * 2.54;
}

// ─── Format Weight (kg or lbs) ────────────────────────────────────────────────
export function formatWeight(kg, unit = 'kg') {
  if (kg === null || kg === undefined) return '—';
  if (unit === 'lbs') {
    return kgToLbs(kg).toFixed(1);
  }
  return kg.toFixed(1);
}

// ─── Parse Weight String (kg or lbs) ──────────────────────────────────────────
export function parseWeight(str, unit = 'kg') {
  if (!str) return null;
  const num = parseFloat(str);
  if (isNaN(num)) return null;
  if (unit === 'lbs') {
    return lbsToKg(num);
  }
  return num;
}

// ─── Format Weight with Unit Label ─────────────────────────────────────────────
export function formatWeightWithUnit(kg, unit = 'kg') {
  const formatted = formatWeight(kg, unit);
  if (formatted === '—') return formatted;
  return `${formatted} ${unit}`;
}

// ─── Format Date (relative or full) ────────────────────────────────────────────
export function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();

  // Today
  if (d.toDateString() === now.toDateString()) {
    return 'Today';
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }

  // Same week
  const weekAgo = new Date(now);
  weekAgo.setDate(weekAgo.getDate() - 7);
  if (d > weekAgo) {
    return d.toLocaleDateString('en-GB', { weekday: 'short' });
  }

  // Full date
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Format Time ───────────────────────────────────────────────────────────────
export function formatTime(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ─── Get Delta from Previous Entry ─────────────────────────────────────────────
export function getDeltaFromPrevious(entries, currentIndex) {
  if (!entries || entries.length < 2) return 0;
  const current = entries[currentIndex];
  const prev = entries[currentIndex + 1];
  if (!current || !prev) return 0;
  return prev.weight_kg - current.weight_kg;
}

// ─── Get Delta Text with Colour Class ──────────────────────────────────────────
export function getDeltaDisplay(delta, unit = 'kg') {
  const formatted = formatWeight(Math.abs(delta), unit);
  if (delta > 0) {
    return { text: `+${formatted}`, class: 'text-error' };
  } else if (delta < 0) {
    return { text: `${formatted}`, class: 'text-green-400' };
  }
  return { text: '0.0', class: 'text-on-surface-variant' };
}

// ─── Unit Toggle Helpers ───────────────────────────────────────────────────────
export const unitLabels = {
  kg: { singular: 'kg', plural: 'kg', label: 'Metric (kg)' },
  lbs: { singular: 'lb', plural: 'lbs', label: 'Imperial (lbs)' }
};
